import { useCallback, useEffect, useRef, useState } from 'react';
import { isApiError } from '@aiptyg/types';
import * as settingsService from '../services/settings.service';
import type {
  CustomerServiceConfigDTO,
  CustomerServiceConfigUpdateParams,
} from '../types/settings.types';
import { useToastStore } from '../../../store/toast.store';

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      network_error: '网络连接失败，请检查网络',
      'invalid customer service config':
        '客服配置无效，请检查安装脚本（完整 LiveChat 代码及有效 license）、链接、渠道状态和原客服脚本；被启用地区使用的渠道不能停用或删除',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

const EMPTY_FORM: CustomerServiceConfigUpdateParams = {
  channel_id: '',
  channels: [],
  market_id: 'CN',
  enabled: true,
  script: '',
};

function dtoToForm(
  dto: CustomerServiceConfigDTO,
  marketId = 'CN',
): CustomerServiceConfigUpdateParams {
  const target = marketId.trim() || 'CN';
  const item = dto.items?.find((entry) => (entry.market_id || 'CN') === target);
  return {
    channel_id: item?.channel_id ?? '',
    channels: dto.channels ?? [],
    market_id: target,
    enabled: item?.enabled ?? (target === 'CN' ? dto.enabled : false),
    script: item?.script ?? (target === 'CN' ? (dto.script ?? '') : ''),
  };
}

export function useCustomerServiceConfig() {
  const addToast = useToastStore((s) => s.addToast);
  const [data, setData] = useState<CustomerServiceConfigDTO | null>(null);
  const [form, setForm] =
    useState<CustomerServiceConfigUpdateParams>(EMPTY_FORM);
  const [selectedMarketId, setSelectedMarketId] = useState('CN');
  const selectedMarket = useRef('CN');
  const [dirty, setDirty] = useState(false);
  const requestGeneration = useRef(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const generation = ++requestGeneration.current;
    setLoading(true);
    setError(null);
    try {
      const result = await settingsService.getCustomerServiceConfig();
      if (generation !== requestGeneration.current) return;
      setData(result);
      setForm(dtoToForm(result, selectedMarket.current));
      setDirty(false);
    } catch (e) {
      if (generation === requestGeneration.current) setError(resolveError(e));
    } finally {
      if (generation === requestGeneration.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    return () => {
      requestGeneration.current++;
    };
  }, [load]);

  const updateField = useCallback(
    <K extends keyof CustomerServiceConfigUpdateParams>(
      key: K,
      value: CustomerServiceConfigUpdateParams[K],
    ) => {
      setDirty(true);
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const selectMarket = useCallback(
    (marketId: string) => {
      if (
        saving ||
        (dirty && !window.confirm('切换地区会放弃未保存的修改，是否继续？'))
      )
        return;
      const nextMarketId = marketId.trim() || 'CN';
      selectedMarket.current = nextMarketId;
      setDirty(false);
      setSelectedMarketId(nextMarketId);
      if (data) {
        setForm(dtoToForm(data, nextMarketId));
      } else {
        setForm((prev) => ({ ...prev, market_id: nextMarketId }));
      }
    },
    [data, dirty, saving],
  );

  const save = useCallback(async () => {
    if (saving) return false;
    if (form.enabled && !form.channel_id && form.script.trim() === '') {
      addToast('error', '启用在线客服时必须填写完整 script 标签');
      return false;
    }

    setSaving(true);
    try {
      const result = await settingsService.updateCustomerServiceConfig({
        market_id: selectedMarketId,
        enabled: form.enabled,
        script: form.script.trim(),
        channel_id: form.channel_id,
        channels: form.channels.map((c) => ({
          ...c,
          name: c.name.trim(),
          url: c.url.trim(),
          script: c.script?.trim(),
        })),
      });
      setData(result);
      setForm(dtoToForm(result, selectedMarketId));
      setDirty(false);
      addToast('success', '在线客服配置已保存，App 和官网下一次打开客服时生效');
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setSaving(false);
    }
  }, [addToast, form, selectedMarketId, saving]);

  return {
    data,
    dirty,
    form,
    loading,
    saving,
    error,
    selectedMarketId,
    updateField,
    selectMarket,
    save,
    reload: load,
  };
}
